import {groupByUniqueKey} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {MolbioFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {DEGENERATE_BASES} from 'biojs/BioConstants';
import {NucleotideType} from 'biojs/Util';
import {MonomerMapping} from 'bulk-import/panes/MonomerMappingForm';
import {ModifiedNucleotideAttributes} from 'constants/SequenceConstants';
import {MonomerNaturalAnalogue} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import staticURL from 'lib/staticURL';
import {
  DEGENERATE_BASE,
  MONOMER_QUERY,
  NaturalAnalogueToNucleotideComponent,
} from 'molbio/monomers/MonomerConstants';
import strUtil from 'util/strUtil';

import {MonomerQuery} from './__generated/MonomerQuery';
import {NucleotideComponent} from './MonomerConstants';
import {APINaturalAnalogue} from './MonomersDAO';

type Monomer = MonomerQuery['monomers'][number];

export function areMonomersEnabledForUser(): boolean {
  return (
    BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES &&
    isFeatureEnabled({feature: MolbioFeature.MOLBIO_OPEN})
  );
}

export function queryMatchesMonomerSymbol(monomerSymbol: string | undefined, query: string): boolean {
  return strUtil.caseInsensitiveContains(monomerSymbol ?? '', query);
}

export function queryMatchesMonomerName(monomerName: string | undefined, query: string): boolean {
  return strUtil.caseInsensitiveContains(monomerName ?? '', query);
}

export function isMonomerNucleotideComponent(
  monomer: Monomer,
  nucleotideComponent: NucleotideComponent,
  nucleotideType?: NucleotideType
): boolean {
  if (
    nucleotideComponent === NucleotideComponent.BASE &&
    !BenchlingConfig.MOLBIO_ENABLE_UT_FLEXIBILITY &&
    ((nucleotideType === NucleotideType.RNA && monomer.naturalAnalogue === MonomerNaturalAnalogue.T) ||
      (nucleotideType === NucleotideType.DNA && monomer.naturalAnalogue === MonomerNaturalAnalogue.U))
  ) {
    return false;
  }
  return NaturalAnalogueToNucleotideComponent[monomer.naturalAnalogue] === nucleotideComponent;
}

// Keep in sync with version in benchling.molbio.monomers.util
export function isNaturalMonomer(monomer: Monomer, nucleotideType: NucleotideType): boolean {
  if (!monomer.isNatural) {
    return false;
  }

  if ([MonomerNaturalAnalogue.R_LOWER, MonomerNaturalAnalogue.U].includes(monomer.naturalAnalogue)) {
    return nucleotideType === NucleotideType.RNA;
  }
  if ([MonomerNaturalAnalogue.D_LOWER, MonomerNaturalAnalogue.T].includes(monomer.naturalAnalogue)) {
    return nucleotideType === NucleotideType.DNA;
  }

  return true;
}

export function isNaturalMonomerId(monomerApiId: string | null, nucleotideType: NucleotideType): boolean {
  if (!monomerApiId) {
    return false;
  }

  const naturalIds = [
    getNaturalMonomerId(MonomerNaturalAnalogue.P_LOWER),
    getNaturalMonomerId(MonomerNaturalAnalogue.A),
    getNaturalMonomerId(MonomerNaturalAnalogue.C),
    getNaturalMonomerId(MonomerNaturalAnalogue.G),
    getNaturalMonomerId(
      nucleotideType === NucleotideType.DNA ? MonomerNaturalAnalogue.D_LOWER : MonomerNaturalAnalogue.R_LOWER
    ),
    getNaturalMonomerId(
      nucleotideType === NucleotideType.DNA ? MonomerNaturalAnalogue.T : MonomerNaturalAnalogue.U
    ),
  ];

  return naturalIds.includes(monomerApiId);
}

export function nucleotideIsNatural(
  mod: ModifiedNucleotideAttributes,
  nucleotideType: NucleotideType
): boolean {
  return (
    isNaturalMonomerId(mod.sugarMonomerId, nucleotideType) &&
    isNaturalMonomerId(mod.baseMonomerId, nucleotideType) &&
    isNaturalMonomerId(mod.phosphateMonomerId, nucleotideType)
  );
}

export function getNaturalPhosphateMonomer(monomers: Array<Monomer>): Monomer {
  const monomer = monomers.filter(
    (m) => m.isNatural && m.naturalAnalogue === MonomerNaturalAnalogue.P_LOWER
  )[0];
  assert(Boolean(monomer), 'Could not find natural phosphate monomer');
  return monomer;
}

export function getNaturalSugarMonomer(monomers: Array<Monomer>, nucleotideType: NucleotideType): Monomer {
  const naturalAnalogue =
    nucleotideType === NucleotideType.RNA ? MonomerNaturalAnalogue.R_LOWER : MonomerNaturalAnalogue.D_LOWER;
  const monomer = monomers.filter((m) => m.isNatural && m.naturalAnalogue === naturalAnalogue)[0];
  assert(Boolean(monomer), 'Could not find natural sugar monomer');
  return monomer;
}

export function getNaturalBaseMonomer(monomers: Array<Monomer>, base: string): Monomer | undefined {
  const monomer = monomers.filter((m) => m.isNatural && m.naturalAnalogue === base.toUpperCase())[0];
  assert(
    Boolean(monomer) || DEGENERATE_BASES.includes(base),
    `Could not find natural base monomer for ${base}`
  );
  return monomer;
}

export function getNaturalMonomerId(naturalAnalogue: MonomerNaturalAnalogue): string {
  const monomerId = BenchlingConfig.MODBIO_NATURAL_MONOMER_IDS_BY_NATURAL_ANALOGUE[naturalAnalogue];
  assert(monomerId != null, `Could not find natural monomer ID for ${naturalAnalogue}`);
  return monomerId;
}

export function getSugarName(
  mod: ModifiedNucleotideAttributes | null,
  monomers: Array<Monomer>,
  nucleotideType: NucleotideType
): string {
  if (mod != null) {
    return monomers.find((monomer) => monomer.id === mod.sugarMonomerId)?.name ?? 'Unknown';
  } else {
    return nucleotideType === NucleotideType.RNA ? 'Ribose' : 'Deoxyribose';
  }
}

export function getPhosphateName(mod: ModifiedNucleotideAttributes | null, monomers: Array<Monomer>): string {
  if (mod != null) {
    if (!mod.phosphateMonomerId) {
      return 'None';
    }
    return monomers.find((monomer) => monomer.id === mod.phosphateMonomerId)?.name ?? 'Unknown';
  } else {
    return 'Phosphate';
  }
}

export function getBaseName(
  mod: ModifiedNucleotideAttributes | null,
  monomers: Array<Monomer>,
  baseSymbol: string
): string {
  let matchingMonomer;
  if (mod != null) {
    matchingMonomer = monomers.find((monomer) => monomer.id === mod.baseMonomerId);
  } else {
    matchingMonomer = monomers.find((monomer) => monomer.symbol === baseSymbol.toUpperCase());
  }
  return matchingMonomer?.name ?? 'Unknown';
}

export async function replaceNaturalSugarMonomersForModifiedNucleotides(
  modifiedNucleotides: Readonly<Array<ModifiedNucleotideAttributes>>,
  sourceNucleotideType: NucleotideType,
  targetNucleotideType: NucleotideType
): Promise<Array<ModifiedNucleotideAttributes>> {
  if (modifiedNucleotides.length === 0) {
    return [];
  } else if (sourceNucleotideType === targetNucleotideType) {
    return modifiedNucleotides.map((monomer) => ({...monomer}));
  }
  const {monomers} = await doQuerySafe<MonomerQuery>({query: MONOMER_QUERY});
  const idToMonomer = groupByUniqueKey(monomers, (monomer) => monomer.id);
  const sourceNaturalSugarMonomer = getNaturalSugarMonomer(monomers, sourceNucleotideType);
  const targetNaturalSugarMonomer = getNaturalSugarMonomer(monomers, targetNucleotideType);

  return modifiedNucleotides
    .map((modifiedNucleotide) => ({
      ...modifiedNucleotide,
      sugarMonomerId:
        modifiedNucleotide.sugarMonomerId === sourceNaturalSugarMonomer.id
          ? targetNaturalSugarMonomer.id
          : modifiedNucleotide.sugarMonomerId,
    }))
    .filter(
      // Filter out modified nucleotides where all monomers are natural.
      (modifiedNucleotide) =>
        modifiedNucleotide.phosphateMonomerId == null ||
        [
          idToMonomer[modifiedNucleotide.sugarMonomerId],
          idToMonomer[modifiedNucleotide.baseMonomerId],
          idToMonomer[modifiedNucleotide.phosphateMonomerId],
        ].some((monomer) => !isNaturalMonomer(monomer, targetNucleotideType))
    );
}

function formatSymbol(monomerSymbol: string): string {
  return monomerSymbol.length <= 1 ? monomerSymbol : `[${monomerSymbol}]`;
}

/**
 * Get the `nucleotideId` variable for a GetNucleotideImageQuery, composed of the API IDs of the sugar, base, and phosphate.
 * Keep in sync with `ModifiedNucleotideNode` in `benchling/molbio/graphql/graphql_resolvers.py`
 */
export function getNucleotideId(
  sugar: Monomer | null,
  base: Monomer | typeof DEGENERATE_BASE | null,
  phosphate: Monomer | null
): string {
  const baseId = !base || base === DEGENERATE_BASE ? '' : base.id;
  return `${sugar?.id ?? ''},${baseId},${phosphate?.id ?? ''}`;
}

function monomerGroupForBase(
  base: string,
  nucleotideType: NucleotideType,
  monomers: Array<Monomer>,
  idToMonomer: {[id: string]: Monomer},
  modifiedNucleotide?: ModifiedNucleotideAttributes
): string {
  let sugarMonomerSymbol: string;
  let baseMonomerSymbol: string;
  let phosphateMonomerSymbol: string;

  if (modifiedNucleotide) {
    sugarMonomerSymbol = idToMonomer[modifiedNucleotide.sugarMonomerId].symbol;
    if (modifiedNucleotide.baseMonomerId === DEGENERATE_BASE) {
      baseMonomerSymbol = base;
    } else {
      baseMonomerSymbol = idToMonomer[modifiedNucleotide.baseMonomerId].symbol;
    }
    phosphateMonomerSymbol = modifiedNucleotide.phosphateMonomerId
      ? idToMonomer[modifiedNucleotide.phosphateMonomerId].symbol
      : '';
  } else {
    sugarMonomerSymbol = getNaturalSugarMonomer(monomers, nucleotideType).symbol;
    const baseMonomer = getNaturalBaseMonomer(monomers, base);
    assert(baseMonomer !== undefined, `Could not find natural base monomer for ${base}`);
    baseMonomerSymbol = baseMonomer.symbol;
    phosphateMonomerSymbol = getNaturalPhosphateMonomer(monomers).symbol;
  }

  return `${formatSymbol(sugarMonomerSymbol)}(${formatSymbol(baseMonomerSymbol)})${formatSymbol(
    phosphateMonomerSymbol
  )}`;
}

// Keep in sync with benchling/molbio/helm/generators.py
export function generateSimplePolymerHELM(
  sequenceBases: string,
  nucleotideType: NucleotideType,
  modifiedNucleotides: Readonly<Array<Readonly<ModifiedNucleotideAttributes>>>,
  monomers: Array<Monomer>
): string {
  const positionToModifiedNucleotide: {[position: number]: ModifiedNucleotideAttributes} = {};
  const idToMonomer: {[id: string]: Monomer} = {};

  modifiedNucleotides.forEach((modifiedNucleotide) => {
    positionToModifiedNucleotide[modifiedNucleotide.position] = modifiedNucleotide;
  });
  monomers.forEach((monomer) => {
    idToMonomer[monomer.id] = monomer;
  });

  return Array.from(sequenceBases)
    .map((base: string, idx: number) =>
      monomerGroupForBase(base, nucleotideType, monomers, idToMonomer, positionToModifiedNucleotide[idx])
    )
    .join('.');
}

export function getMonomerIdFromMappingOrNaturalBase(
  naturalAnalogue: keyof MonomerMapping,
  monomers: Array<Monomer>,
  monomerMapping?: MonomerMapping | null
): string {
  if (monomerMapping != null) {
    const mappingMonomerId = monomerMapping[naturalAnalogue];
    if (monomers.find((monomer) => monomer.id === mappingMonomerId) != null) {
      return mappingMonomerId;
    }
  }
  return getNaturalBaseMonomer(monomers, naturalAnalogue)!.id;
}

export const HELM_LIBRARY_LINK = staticURL('/static/monomers/HELMCoreLibrary.json');

export interface CoreLibMonomer {
  author: string;
  createDate: string | null;
  id: number;
  molfile: string;
  monomerType: string;
  name: string;
  naturalAnalog: APINaturalAnalogue;
  polymerType: string;
  rgroups: Array<{
    alternateId: string;
    capGroupName: string;
    capGroupSMILES: string;
    id?: number;
    label: string;
  }>;
  smiles: string;
  symbol: string;
}

export const HELMCoreMonomerLibraryAPI = {
  fetch: async (): Promise<Array<CoreLibMonomer>> => {
    const resp = await fetch(HELM_LIBRARY_LINK);
    return (await resp.json()) as Array<CoreLibMonomer>;
  },
};

export function gqlToAPINaturalAnalogue(
  gqlNaturalAnalogue: MonomerNaturalAnalogue | undefined
): APINaturalAnalogue | undefined {
  switch (gqlNaturalAnalogue) {
    case MonomerNaturalAnalogue.R_LOWER:
      return 'r';
    case MonomerNaturalAnalogue.D_LOWER:
      return 'd';
    case MonomerNaturalAnalogue.P_LOWER:
      return 'p';
    default:
      return gqlNaturalAnalogue;
  }
}
